import { lazy, Suspense, useId } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { useShell } from '../store'
import { Skeleton } from './page'

const NotificationsTab = lazy(() => import('@/pages/app/my-work').then(m => ({ default: m.NotificationsTab })))

export const NOTIFICATIONS_PARAM = 'notifications'

/**
 * PRD-018 §3.3 (UX audit 2026-09-19 P1-7): the bell "opens the Notifications tab of My work in a drawer over the
 * current page, so the person does not lose their place". The drawer is `?notifications=1` on whatever route is
 * open, rendered by the frame in the same shell the work-item drawer uses (right side, 560 px, Escape closes), and
 * Escape or the close button drops the param and leaves the route as it was. `/my-work/notifications` stays the
 * full page and the deep link. Prior art: Linear's inbox and GitHub's notifications open as an overlay from the
 * bell; Karbon's Triage slides in over the work list.
 */
export function useNotificationsDrawer() {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const open = params.get(NOTIFICATIONS_PARAM) === '1'
  const onPage = location.pathname === '/my-work/notifications'
  return {
    open,
    show: () => {
      if (onPage)
        return
      const next = new URLSearchParams(params)
      next.set(NOTIFICATIONS_PARAM, '1')
      setParams(next)
    },
    hide: () => {
      const next = new URLSearchParams(params)
      next.delete(NOTIFICATIONS_PARAM)
      navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' }, { replace: true })
    },
  }
}

export function NotificationsDrawer() {
  const { open, hide } = useNotificationsDrawer()
  const { bootstrap } = useShell()
  const titleId = useId()
  if (!open)
    return null
  return (
    <ModalOverlay isOpen isDismissable onOpenChange={o => !o && hide()} className="fixed inset-0 z-40 flex justify-end bg-overlay/20">
      <Modal className="h-full w-[min(560px,100vw)] border-l border-secondary bg-primary shadow-xl">
        <Dialog aria-labelledby={titleId} className="flex h-full flex-col outline-none" data-testid="notifications-drawer">
          <header className="flex items-center justify-between gap-3 border-b border-secondary px-6 py-3">
            <h2 id={titleId} className="text-sm font-semibold text-primary">
              Notifications
              {bootstrap.counts.notificationsUnread > 0 ? <span className="ml-2 rounded-full px-1.5 text-xs font-normal" style={{ background: 'var(--shell-hot-bg)', color: 'var(--shell-hot-fg)' }}>{bootstrap.counts.notificationsUnread}</span> : null}
            </h2>
            <span className="flex items-center gap-2">
              <Link to="/my-work/notifications" className="text-xs text-tertiary hover:text-primary" data-testid="notifications-drawer-page">Open My work</Link>
              <Button size="sm" color="tertiary" onClick={hide} aria-label="Close notifications" data-testid="notifications-drawer-close">Close</Button>
            </span>
          </header>
          <div className="min-h-0 flex-1 overflow-auto">
            <Suspense fallback={<div className="p-6"><Skeleton rows={4} /></div>}>
              <NotificationsTab />
            </Suspense>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
